import { organizationStore } from '@entities/organization';
import SplashScreen from '@pages/pure-loading-screen';
import * as Sentry from '@sentry/react';
import { MOBILE_FCM_TOKEN_KEY } from '@shared/constants/local-storage';
import { PostMessageType } from '@shared/constants/notifications';
import { CUSTOM_AUTH_DOMAIN_CACHE_KEY } from '@shared/constants/white-label';
import {
	useEventListener,
	useLocalStorage,
	useMounted,
} from '@shared/design-system/mantine/hooks.ts';
import { getGlobalRefreshTokenKey, getMetadataUrl } from '@shared/utils/auth';
import { getDomain } from '@shared/utils/cookies';
import {
	deserializeLocalStorageValue,
	serializeLocalStorageValue,
} from '@shared/utils/localStorage';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AuthProviderProps as ConfigsProps } from 'react-oidc-context';
import { AuthProvider as OIDCProvider, useAuth } from 'react-oidc-context';

import { LOCAL_STORAGE_PROFILE_HOST_KEY } from '@shared/constants/auth';
import {
	COOKIES_PROFILE_HOST_KEY,
	getDefaultAuthConfigs,
	signoutFallback,
	unsubscribeNotification,
} from './configs';

export interface AuthWrapperProps {
	bypassAuthRedirect?: boolean;
	children: ReactNode;
	customSigninRedirect?: () => Promise<void> | void;
}

/* Redirection, authentication related caches and timeout handling */
const Wrapper = ({
	bypassAuthRedirect,
	customSigninRedirect,
	children,
}: AuthWrapperProps) => {
	const {
		isLoading,
		isAuthenticated,
		user,
		signinRedirect,
		removeUser,
		signoutRedirect,
		error,
	} = useAuth();

	CUSTOM_AUTH_DOMAIN_CACHE_KEY;
	const [fcmToken, , removeFcmToken] = useLocalStorage({
		key: MOBILE_FCM_TOKEN_KEY,
	});
	const [, setProfileHostKey] = useLocalStorage({
		key: LOCAL_STORAGE_PROFILE_HOST_KEY,
		serialize: String,
		deserialize: String,
	});

	const onConnected = useCallback(
		async (callback?: () => Promise<void> | void) => {
			const metadataUrl = getMetadataUrl();
			const deter = metadataUrl
				? await fetch(metadataUrl)
						.then(() => true)
						.catch(() => false)
				: true;
			if (deter) {
				void callback?.();
			} else {
				Sentry.captureMessage(
					`[OIDC Service fail to Connect]: ${metadataUrl}`,
					'debug'
				);
				console.debug('[OIDC Service fail to Connect]: ', metadataUrl);
			}
		},
		[]
	);

	const onLogout = useCallback(
		async (fallback?: () => void) => {
			try {
				await onConnected(async () => {
					await removeUser?.();
					await signoutRedirect?.();
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				console.error('[Auth] onLogout Error: ', e);
				Sentry.captureMessage(`[Auth] onLogout Error: ${e?.message}`, 'error');
				fallback?.();
			}
		},
		[onConnected, removeUser, signoutRedirect]
	);

	// oidc will take care of the refresh token process if automaticSilentRenew is set to true in configs
	// comment out this useEffect as it will cause the page to refresh whenever a token is expiring and closes modal, drawer
	// useEffect(() => {
	// 	return events.addAccessTokenExpiring(() => {
	// 		signinSilent?.();
	// 	});
	// }, [events, signinSilent]);

	useEffect(() => {
		if (user) {
			serializeLocalStorageValue(
				getGlobalRefreshTokenKey(),
				get(user, 'refresh_token')
			);

			/* Pass user to react native app */
			window?.ReactNativeWebView?.postMessage(
				JSON.stringify({
					type: PostMessageType.USER,
					data: user,
				})
			);
		}
	}, [user]);

	useEffect(() => {
		/* Get Auth domain from localStorage */
		const authDomain =
			deserializeLocalStorageValue(CUSTOM_AUTH_DOMAIN_CACHE_KEY) ||
			import.meta.env.VITE_AUTH_BASE_URL;

		// Set cookies for current domain for keycloak calling backend api
		Cookies.set(
			COOKIES_PROFILE_HOST_KEY,
			import.meta.env.VITE_PROFILE_API_BASE_URL,
			{
				expires: 400,
				domain: getDomain(authDomain, { exact: true }),
			}
		);
		// Set localStorage for current domain for keycloak calling backend api as React Native Auth flow not sharing cookies with webapp
		setProfileHostKey(import.meta.env.VITE_PROFILE_API_BASE_URL);

		if (!isLoading && !isAuthenticated && !bypassAuthRedirect) {
			if (customSigninRedirect) {
				void onConnected(customSigninRedirect);
			} else {
				void onConnected(signinRedirect);
			}
		}
	}, [
		bypassAuthRedirect,
		isLoading,
		isAuthenticated,
		setProfileHostKey,
		onConnected,
		signinRedirect,
		customSigninRedirect,
	]);

	const onError = useCallback(
		async (error: Error) => {
			console.error('[Auth Error]', error);

			if (fcmToken) {
				await unsubscribeNotification(fcmToken);
				removeFcmToken();
			} else {
				await unsubscribeNotification();
			}

			// post message after remove token from backend
			window?.ReactNativeWebView?.postMessage(
				JSON.stringify({
					type: PostMessageType.LOGOUT,
				})
			);

			Sentry.captureMessage(
				`[OIDC Service] onError: ${error?.message}`,
				'error'
			);
			switch (error?.message) {
				/**
				 * After adding delete token logic, oidc context sometime missing state
				 * Therefore, added a work around to trigger reload by reading the error message
				 */
				case 'No matching state found in storage':
					signoutFallback();
					break;
				default:
					await onLogout(signoutFallback);
					break;
			}
		},
		[fcmToken, onLogout, removeFcmToken]
	);

	useEffect(() => {
		if (error) {
			void onError(error);
		}
	}, [error, onError]);

	if ((isLoading || !isAuthenticated || !user) && !bypassAuthRedirect) {
		return <SplashScreen />;
	}

	return children;
};

export interface AuthProviderProps extends Pick<
	AuthWrapperProps,
	'customSigninRedirect'
> {
	publicRoutes?: string[];
	children: ReactNode;
	oidcConfigs?: ConfigsProps;
}

/* Authentication with custom domains configuration */
const Component = ({
	publicRoutes = [],
	children,
	customSigninRedirect,
	oidcConfigs = getDefaultAuthConfigs(),
}: AuthProviderProps) => {
	const [bypassAuthRedirect, setBypassAuthRedirect] = useState(
		publicRoutes.some((prefix) => window.location.pathname?.startsWith(prefix))
	);

	useEventListener('popstate' as keyof HTMLElementEventMap, () => {
		const pathname = window.location.pathname;
		setBypassAuthRedirect(
			publicRoutes.some((prefix) => pathname.startsWith(prefix))
		);
	});

	return (
		<OIDCProvider {...oidcConfigs}>
			<Wrapper
				bypassAuthRedirect={bypassAuthRedirect}
				customSigninRedirect={customSigninRedirect}
			>
				{children}
			</Wrapper>
		</OIDCProvider>
	);
};

/* Handle white label custom domains before doing any authentication */
const AuthProvider = ({ oidcConfigs, ...props }: AuthProviderProps) => {
	const mounted = useMounted();

	const [configs, setConfigs] = useState<ConfigsProps | undefined>(oidcConfigs);
	const [isFetched, setIsFetched] = useState(false);

	const fetchWhiteLabelConfigs = async () => {
		if (configs) {
			setIsFetched(true);
			return;
		}

		try {
			let authDomain: undefined | string;
			if (location.origin && location.hostname !== 'localhost') {
				authDomain = (
					await organizationStore.getWhiteLabel({
						domain: location.origin,
					})
				)?.authDomain;
			}

			if (authDomain) {
				/* Determine whether the authentication service works under custom auth prefix */
				const metadataUrl = getMetadataUrl(authDomain);
				const deter = await fetch(metadataUrl)
					.then(() => true)
					.catch(() => false);

				authDomain = deter && authDomain ? authDomain : undefined;
			}

			/* Using white label auth domain to fetch the auth metadata */
			if (authDomain) {
				/* Handle custom domain and custom authentication URL prefix */
				serializeLocalStorageValue(CUSTOM_AUTH_DOMAIN_CACHE_KEY, authDomain);
				await new Promise((resolve) => setTimeout(resolve, 0));
			}
			const _configs = getDefaultAuthConfigs(authDomain);
			setConfigs(_configs);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			/* Fallback to default auth configs for fetching metadata if get white label information error */
			console.error('[fetchWhiteLabelConfigs] failed');
			Sentry.captureMessage(
				`[fetchWhiteLabelConfigs] failed: ${e?.message}`,
				'error'
			);
			const _configs = getDefaultAuthConfigs();
			setConfigs(_configs);
		} finally {
			setIsFetched(true);
		}
	};

	useEffect(() => {
		if (mounted) {
			fetchWhiteLabelConfigs();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mounted]);

	if (!isFetched) return <SplashScreen />;

	return <Component oidcConfigs={configs} {...props} />;
};

export default AuthProvider;
